/**
 * 협력사 홈페이지 API 서비스
 */
import { apiClient } from '../../../core/api/client';
import { commonCodeApi } from '../../../core/api/commonCodeApi';
import { getSlotCountByDay } from '../../../core/types/commonCode.types';
import {
    SaveSettingsRequest,
    SaveSettingsResponse,
    WeeklyScoreRequest,
    WeeklyScoreResponse,
    DailyStatusRequest,
    DailyStatusResponse,
    RiderStatusRequest,
    RiderStatusResponse,
    RiderStats,
    RiderData,
    RiderStatus,
    DayData,
    DayTimeSlotData,
    WeeklyStats,
    DayOfWeek,
    DEFAULT_WEEKLY_STATS,
    DEFAULT_DAILY_STATS,
    DEFAULT_RIDER_STATS,
    DEFAULT_DAILY_DATA,
    DEFAULT_TIME_SLOTS, DailyStats, TimeSlotStatus, TimeSlot,
} from '../types/homePageForSupplier';

const BASE_PATH = '/api/v1/mob/supplier';

/**
 * 모바일 협력사 정보 조회 API 응답 타입
 */
interface MobSupplierInfoApiResponse {
    success: boolean;
    message?: string;
    supplier: {
        suppId: string;
        suppName: string;
        statusCode: string;
        suppNum: string;
        suppCompName: string;
        compCeoName: string;
        managerName: string;
        mngPhoneNum: string;
        deliveryArea: string;
        accountNum: string;
        bankCode: string;
        bankName: string;
        preAdjustYn: string;
        addrInfo: string;
        suppLvl: string;
        gradeName: string;
        insDate: string;
        endMngDay: string;
        dpBankCd: string;
        dpAcctNo: string;
        dpAccName: string;
        slotSetCnt: number | null;  // 슬롯 세트 수
    } | null;
}

export interface GetSupplierInfoResponse {
    suppId: string;
    suppName: string;
    slotSetCnt: number;  // 슬롯 세트 수 (기본값 1)
    success: boolean;
    message?: string;
}

/**
 * 협력사 정보 조회 (모바일용)
 * GET /api/v1/mob/supplier/info
 *
 * 토큰에서 suppId가 자동으로 추출되므로 suppId 파라미터는 사용되지 않습니다.
 */
export const getSupplierInfo = async (_suppId?: string): Promise<GetSupplierInfoResponse> => {
    try {
        const adminViewSuppId = localStorage.getItem('ADMIN_VIEW_SUPP_ID');
        const params = adminViewSuppId ? { suppId: adminViewSuppId } : undefined;
        
        const response = await apiClient.get<MobSupplierInfoApiResponse>(
            `${BASE_PATH}/info`,
            params ? { params } : undefined
        );

        if (!response.data.success || !response.data.supplier) {
            console.warn('[getSupplierInfo] API 응답 실패:', response.data.message);
            return {
                suppId: '',
                suppName: '',
                slotSetCnt: 1,
                success: false,
                message: response.data.message || '협력사 정보를 찾을 수 없습니다.',
            };
        }

        // slotSetCnt가 null이거나 0이면 기본값 1 사용
        const slotSetCnt = response.data.supplier.slotSetCnt || 1;

        return {
            suppId: response.data.supplier.suppId,
            suppName: response.data.supplier.suppName,
            slotSetCnt,
            success: true,
        };
    } catch (error) {
        return {
            suppId: '',
            suppName: '',
            slotSetCnt: 1,  // 기본값
            success: false,
            message: '협력사 정보를 조회하는 중 오류가 발생했습니다.',
        };
    }
};

/**
 * 백엔드 대시보드 API 응답 타입
 */
interface DashboardSummaryApiResponse {
    deliveryDate: string;
    totFinishCnt: number;
    totRejectCnt: number;
    totAssignCancelCnt: number;
    totDeliCancelCnt: number;
    totRiderCnt: number;
    totMorTimeCnt: number;
    totAfterNonCnt: number;
    totNightCnt: number;
    totNightNonCnt: number;
    totMorTimeSlot: number;
    totAfterNonSlot: number;
    totNightSlot: number;
    totNightNonSlot: number;
    totSlotSet: number;
    statusSummary: Array<{
        deliveryStatus: string;
        name: string;
        riderCnt: number;
    }>;
    success: boolean;
    message?: string;
}

interface DashboardWeeklyRidersApiResponse {
    weekStartDate: string;
    weekEndDate: string;
    riderPerformances: Array<{
        riderId: string;
        riderName: string;
        riderPhone: string;
        deliveryStatus: string;
        totalCnt: number;
        finishCnt: number;
        acceptanceRate: number;
        dailySlots: Record<string, number[]>;
        finishFoodCnt?: number;
        finishBmartCnt?: number;
        finishStoreCnt?: number;
    }>;
    success: boolean;
    message?: string;
}

/**
 * 주간 대시보드 API 응답 타입
 */
interface DashboardWeeklyApiResponse {
    weekStartDate: string;
    weekEndDate: string;
    totalFinishCnt: number;
    periodSlotAchievementRate: number | null;
    periodMorTimeCnt: number;
    periodAfterNonCnt: number;
    periodNightCnt: number;
    periodNightNonCnt: number;
    periodMorTimeRate: number | null;
    periodAfterNonRate: number | null;
    periodNightRate: number | null;
    periodNightNonRate: number | null;
    periodAcceptanceRate: number | null;
    dailyPerformances: Array<{
        deliveryDate: string;
        finishCnt: number;
        rejectCnt: number;
        morTimeCnt: number;
        afterNonCnt: number;
        nightCnt: number;
        nightNonCnt: number;
        morTimeRate: number | null;
        afterNonRate: number | null;
        nightRate: number | null;
        nightNonRate: number | null;
        slotAchievementRate: number | null;
        acceptanceRate: number | null;
    }>;
    // 정책 기반 동적 슬롯 데이터 (슬롯명/건수/달성률이 정책에 따라 동적으로 결정됨)
    supplierPerformances?: Array<{
        suppId: string;
        slotNames: string[];
        slotTimeRanges?: string[];                          // 슬롯별 대표 시간 범위 (예: ["05:00 ~ 07:59", ...])
        dailySlotTimeRanges?: Record<string, string[]>;    // 일별 슬롯 시간 범위 (주말/평일 구분)
        dailySlots: Record<string, string[]>;               // { "20260801": ["100%", "85%", "0%"] } 또는 target=0이면 숫자 문자열
        dailySlotCounts: Record<string, string[]>;          // { "20260801": ["10/10", "5/10", "5"] }
        dailyMissionRate?: Record<string, string>;          // requiredSlotCnt 설정 시: { "20260801": "100%" }
    }>;
    success: boolean;
    message?: string;
}

/**
 * 배송 상태 코드를 RiderStatus로 변환
 */
const mapDeliveryStatusToRiderStatus = (deliveryStatus: string): RiderStatus => {
    switch (deliveryStatus) {
        case 'DELIVERING':
        case 'ACTIVE':
            return 'active';
        case 'COMPLETED':
        case 'FINISHED':
            return 'completed';
        default:
            return 'inactive';
    }
};

/**
 * 협력사 설정 저장
 * POST /api/v1/mob/supplier/settings
 */
export const saveSupplierSettings = async (
    params: SaveSettingsRequest
): Promise<SaveSettingsResponse> => {
    // TODO: 실제 API 연동 시 활성화
    // const response = await apiClient.post<SaveSettingsResponse>(
    //     `${BASE_PATH}/settings`,
    //     params
    // );
    // return response.data;

    // 임시 응답
    return {
        success: true,
        message: '설정이 저장되었습니다.',
    };
};

/**
 * 주간 예상 점수 조회
 * GET /api/v1/mob/supplier/weekly-score
 */
export const getWeeklyScore = async (
    params: WeeklyScoreRequest
): Promise<WeeklyScoreResponse> => {
    // TODO: 실제 API 연동 시 활성화
    // const response = await apiClient.get<WeeklyScoreResponse>(
    //     `${BASE_PATH}/weekly-score`,
    //     { params }
    // );
    // return response.data;

    // 임시 응답
    return {
        weeklyStats: DEFAULT_WEEKLY_STATS,
        dailyData: DEFAULT_DAILY_DATA,
        success: true,
    };
};

/**
 * YYYYMMDD 형식의 날짜를 요일(DayOfWeek)로 변환
 */
const dateToDayOfWeek = (dateStr: string): DayOfWeek => {
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)) - 1;
    const day = parseInt(dateStr.substring(6, 8));
    const date = new Date(year, month, day);
    const dayIndex = date.getDay();

    const dayMap: { [key: number]: DayOfWeek } = {
        0: 'sun',
        1: 'mon',
        2: 'tue',
        3: 'wed',
        4: 'thu',
        5: 'fri',
        6: 'sat',
    };
    return dayMap[dayIndex];
};

/**
 * 요일의 한글 라벨 반환
 */
const getDayLabel = (day: DayOfWeek): string => {
    const labels: { [key in DayOfWeek]: string } = {
        wed: '수요일',
        thu: '목요일',
        fri: '금요일',
        sat: '토요일',
        sun: '일요일',
        mon: '월요일',
        tue: '화요일',
    };
    return labels[day];
};

/**
 * 주간 현황 조회
 * GET /api/v1/dashboard/weekly API를 사용하여 주간 현황 데이터를 구성
 *
 * @param params.fromDate - 시작 날짜 (YYYYMMDD 형식)
 * @param params.toDate - 종료 날짜 (YYYYMMDD 형식)
 * @param params.suppId - 협력사 ID
 */
export const getWeeklyStatus = async (
    params: { fromDate: string; toDate: string; suppId: string }
): Promise<WeeklyScoreResponse> => {
    try {
        const adminViewSuppId = localStorage.getItem('ADMIN_VIEW_SUPP_ID');
        const requestParams = adminViewSuppId 
            ? { fromDate: params.fromDate, toDate: params.toDate, suppId: adminViewSuppId }
            : { fromDate: params.fromDate, toDate: params.toDate };
            
        const response = await apiClient.get<DashboardWeeklyApiResponse>(
            '/api/v1/mob/dashboard/weekly',
            { params: requestParams }
        );

        const data = response.data;

        // 요일 순서 정의 (수, 목, 금, 토, 일, 월, 화)
        const dayOrder: DayOfWeek[] = ['wed', 'thu', 'fri', 'sat', 'sun', 'mon', 'tue'];

        // 요일별 인덱스 (수요일 = 0)
        const dayToIndex: { [key in DayOfWeek]: number } = {
            wed: 0, thu: 1, fri: 2, sat: 3, sun: 4, mon: 5, tue: 6,
        };

        // 기본 시간대 데이터
        const defaultTimeSlotData: DayTimeSlotData = { rate: 0, count: 0, target: 0, isAchieved: false, hasTarget: true };

        // 각 시간대 달성률 및 달성 여부 계산 (레거시 4슬롯용)
        const createTimeSlotData = (rate: number | null, count: number): DayTimeSlotData => ({
            rate: rate || 0,
            count: count,
            target: 0,
            isAchieved: (rate || 0) >= 100,
            hasTarget: true,
        });

        // YYYYMMDD 문자열을 Date 객체로 변환
        const parseDate = (dateStr: string): Date => {
            const year = parseInt(dateStr.substring(0, 4));
            const month = parseInt(dateStr.substring(4, 6)) - 1;
            const day = parseInt(dateStr.substring(6, 8));
            return new Date(year, month, day);
        };

        // Date 객체를 YYYYMMDD 문자열로 변환
        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        };

        // ── 정책 기반 동적 슬롯 데이터 추출 ──────────────────────────────
        const DEFAULT_SLOT_NAMES = ['아침점심피크', '오후논피크', '저녁피크', '심야논피크'];
        const suppPerf = data.supplierPerformances?.[0];
        const policySlotNames: string[] = suppPerf?.slotNames?.length ? suppPerf.slotNames : DEFAULT_SLOT_NAMES;
        const dailySlotsMap: Record<string, string[]> = suppPerf?.dailySlots ?? {};
        const dailyCountsMap: Record<string, string[]> = suppPerf?.dailySlotCounts ?? {};
        const missionRateMap: Record<string, string> = suppPerf?.dailyMissionRate ?? {};
        const slotCount = policySlotNames.length;

        // 날짜별 슬롯 배열 파싱: dailySlots(rate)과 dailySlotCounts(건수)를 합쳐서 DayTimeSlotData 구성
        const parseSlotsForDate = (deliveryDate: string): DayTimeSlotData[] => {
            const rateStrs = dailySlotsMap[deliveryDate] ?? [];
            if (rateStrs.length === 0) return policySlotNames.map(() => defaultTimeSlotData);
            const countStrs = dailyCountsMap[deliveryDate] ?? [];
            return rateStrs.map((rateStr, i): DayTimeSlotData => {
                const countStr = countStrs[i] ?? '';
                if (countStr.includes('/')) {
                    // target > 0: "achieved/target"
                    const [achievedStr, targetStr] = countStr.split('/');
                    const count = parseInt(achievedStr, 10) || 0;
                    const target = parseInt(targetStr, 10) || 0;
                    const rate = parseInt(rateStr, 10) || 0;
                    return { rate, count, target, isAchieved: rate >= 100, hasTarget: true };
                }
                // target = 0: 숫자 문자열로 실적 건수만 전달, 달성 판정 없음
                const count = countStr ? parseInt(countStr, 10) || 0 : parseInt(rateStr, 10) || 0;
                return { rate: 0, count, target: 0, isAchieved: false, hasTarget: false };
            });
        };
        // ─────────────────────────────────────────────────────────────────

        // target이 있는 슬롯 수 파악 (수행 건수 0 정책 슬롯은 분모에서 제외)
        let targetSlotCount = slotCount;
        const firstDateWithData = Object.keys(dailySlotsMap)[0];
        if (firstDateWithData) {
            const firstDayParsed = parseSlotsForDate(firstDateWithData);
            const withTarget = firstDayParsed.filter(s => s.hasTarget).length;
            if (withTarget > 0) targetSlotCount = withTarget;
        }

        // API 응답 데이터를 요일별로 매핑
        const dailyDataMap = new Map<DayOfWeek, DayData>();
        let referenceDate: Date | null = null;
        let referenceDayIndex: number | null = null;

        if (data.dailyPerformances && data.dailyPerformances.length > 0) {
            data.dailyPerformances.forEach((perf) => {
                const day = dateToDayOfWeek(perf.deliveryDate);
                const label = getDayLabel(day);

                // 첫 번째 데이터를 기준 날짜로 저장
                if (referenceDate === null) {
                    referenceDate = parseDate(perf.deliveryDate);
                    referenceDayIndex = dayToIndex[day];
                }

                // 정책 기반 동적 슬롯 계산
                const slots = parseSlotsForDate(perf.deliveryDate);
                const missionRate = missionRateMap[perf.deliveryDate];
                let currentSlots: number;
                let totalSlots: number;
                if (missionRate !== undefined) {
                    // 미션 달성 모드 (requiredSlotCnt 설정): 하루 단위 달성 여부
                    currentSlots = missionRate === '100%' ? 1 : 0;
                    totalSlots = 1;
                } else {
                    // target이 있는 슬롯만 달성 여부 판정
                    const targetSlots = slots.filter(s => s.hasTarget);
                    currentSlots = targetSlots.filter(s => s.isAchieved).length;
                    totalSlots = targetSlots.length;
                }

                dailyDataMap.set(day, {
                    day,
                    label,
                    deliveryDate: perf.deliveryDate,
                    acceptanceRate: perf.acceptanceRate || 0,
                    currentSlots,
                    totalSlots,
                    slots,
                    slotNames: policySlotNames,
                    morningPeak: createTimeSlotData(perf.morTimeRate, perf.morTimeCnt),
                    afternoonNonPeak: createTimeSlotData(perf.afterNonRate, perf.afterNonCnt),
                    eveningPeak: createTimeSlotData(perf.nightNonRate, perf.nightNonCnt),
                    night: createTimeSlotData(perf.nightRate, perf.nightCnt),
                    hasData: true,
                });
            });
        }

        // 7개 요일 모두 포함하도록 dailyData 구성 (누락된 요일은 기본값 + 날짜 계산)
        const dailyData: DayData[] = dayOrder.map((day, index) => {
            if (dailyDataMap.has(day)) {
                return dailyDataMap.get(day)!;
            }

            // 누락된 요일의 날짜 계산
            let calculatedDate = '';
            if (referenceDate !== null && referenceDayIndex !== null) {
                const dayDiff = index - referenceDayIndex;
                const targetDate = new Date(referenceDate);
                targetDate.setDate(targetDate.getDate() + dayDiff);
                calculatedDate = formatDate(targetDate);
            }

            return {
                day,
                label: getDayLabel(day),
                deliveryDate: calculatedDate,
                acceptanceRate: 0,
                currentSlots: 0,
                totalSlots: targetSlotCount,
                slots: policySlotNames.map(() => defaultTimeSlotData),
                slotNames: policySlotNames,
                morningPeak: defaultTimeSlotData,
                afternoonNonPeak: defaultTimeSlotData,
                eveningPeak: defaultTimeSlotData,
                night: defaultTimeSlotData,
                hasData: false,
            };
        });

        // 주간 통계 구성 - dailyData에서 슬롯 달성 개수 합산
        const totalAchievedSlots = dailyData.reduce((sum, day) => sum + day.currentSlots, 0);
        const totalWeeklySlots = dailyData.reduce((sum, day) => sum + day.totalSlots, 0);

        const weeklyStats: WeeklyStats = {
            achievedSlots: totalAchievedSlots,
            totalSlots: totalWeeklySlots,
            completedCount: data.totalFinishCnt || 0,
            targetCount: 0,
            acceptanceRate: data.periodAcceptanceRate || 0,
            bottomThreeAverage: 0,
            isAchieved: totalAchievedSlots >= totalWeeklySlots,
            slotAchievementRate: data.periodSlotAchievementRate || 0,
        };

        return {
            weeklyStats,
            dailyData,
            success: true,
        };
    } catch (error) {
        console.error('주간 현황 조회 실패: ', error);
        return {
            weeklyStats: DEFAULT_WEEKLY_STATS,
            dailyData: DEFAULT_DAILY_DATA,
            success: false,
            message: '주간 현황을 조회하는 중 오류가 발생했습니다.',
        };
    }
};


/**
 * 금일 달성현황 조회
 * GET /api/v1/dashboard/summary API를 활용하여 당일 현황 데이터를 구성
 *
 * @param params.date - 조회 날짜 (YYYY-MM-DD 형식)
 * @param params.suppId - 협력사 ID
 * @param params.slotSetCnt - 협력사 슬롯 세트 수 (선택, 기본값 1)
 */
export const getDailyStatus = async (
    params: DailyStatusRequest & { suppId: string; slotSetCnt?: number }
): Promise<DailyStatusResponse> => {
    try {
        // SLOTINFO 조회 (캐시된 데이터 사용)
        const slotInfoList = await commonCodeApi.getSlotInfo();

        // 슬롯 세트 수 (기본값 1)
        const slotSetCnt = params.slotSetCnt || 1;

        // 선택된 날짜 기준 요일 계산 (YYYY-MM-DD 형식)
        // params.date가 없으면 오늘 날짜 사용
        let selectedDate: Date;
        if (params.date) {
            const [year, month, day] = params.date.split('-').map(Number);
            selectedDate = new Date(year, month - 1, day);
        } else {
            selectedDate = new Date();
        }
        const dayOfWeek = selectedDate.getDay(); // 0=일, 1=월, ..., 6=토

        // 슬롯별 기준 건수 찾기 (요일에 맞는 값 × 세트 수)
        const getSlotTarget = (slotCode: string): number => {
            const slotInfo = slotInfoList.find(s => s.slotCode === slotCode);
            if (!slotInfo) return 0;
            const baseCount = getSlotCountByDay(slotInfo, dayOfWeek);
            return baseCount * slotSetCnt;
        };

        const adminViewSuppId = localStorage.getItem('ADMIN_VIEW_SUPP_ID');
        const dateParam = params.date ? params.date.replace(/-/g, '') : undefined;
        const requestParams: any = dateParam ? { date: dateParam } : {};
        if (adminViewSuppId) {
            requestParams.suppId = adminViewSuppId;
        }
        
        const response = await apiClient.get<DashboardSummaryApiResponse>(
            '/api/v1/mob/dashboard/summary',
            Object.keys(requestParams).length > 0 ? { params: requestParams } : undefined
        );

        const data = response.data;

        // 당일 통계 구성
        const completedCount = data.totFinishCnt || 0;
        const rejectedCount = (data.totRejectCnt || 0) + (data.totAssignCancelCnt || 0) + (data.totDeliCancelCnt || 0);
        const totalCount = completedCount + rejectedCount;
        const acceptanceRate = totalCount > 0
            ? Math.round((completedCount / totalCount) * 100 * 10) / 10
            : 0;

        const dailyStats: DailyStats = {
            completedCount,
            rejectedCount,
            acceptanceRate,
        };

        // 현재 시간 기반 슬롯 상태 계산
        const now = new Date();
        const currentHour = now.getHours();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        const calculateSlotStatus = (slotId: string): TimeSlotStatus => {
            if (slotId === 'morning') {
                const endHour = isWeekend ? 14 : 13;
                if (currentHour >= 6 && currentHour < endHour) return 'inProgress';
                if (currentHour >= endHour) return 'completed';
                return 'waiting';
            }
            if (slotId === 'afternoon') {
                const startHour = isWeekend ? 14 : 13;
                if (currentHour >= startHour && currentHour < 17) return 'inProgress';
                if (currentHour >= 17) return 'completed';
                return 'waiting';
            }
            if (slotId === 'evening') {
                if (currentHour >= 17 && currentHour < 20) return 'inProgress';
                if (currentHour >= 20) return 'completed';
                return 'waiting';
            }
            if (slotId === 'night') {
                if (currentHour >= 20 || currentHour < 3) return 'inProgress';
                if (currentHour >= 3 && currentHour < 6) return 'completed';
                return 'waiting';
            }
            return 'waiting';
        };

        // 슬롯 인덱스별 기본 아이콘·시간범위·ID (정책 슬롯에 순서대로 매핑)
        const SLOT_IDS_BY_IDX = ['morning', 'afternoon', 'evening', 'night'];
        const SLOT_ICONS_BY_IDX = ['📦', '📦', '📦', '📦'];
        const SLOT_RANGES_BY_IDX = [
            '평일: 06:00~12:59 / 주말: 06:00~13:59',
            '평일: 13:00~16:59 / 주말: 14:00~16:59',
            '17:00 ~ 19:59, 평일/주말 동일',
            '20:00 ~ 02:59, 평일/주말 동일',
        ];

        // ── 정책 기반 동적 슬롯 (weekly API를 당일 날짜로 호출) ──────────────
        let policySlots: TimeSlot[] | null = null;
        if (dateParam) {
            try {
                const wParams: Record<string, string> = { fromDate: dateParam, toDate: dateParam };
                if (adminViewSuppId) wParams.suppId = adminViewSuppId;
                const wResp = await apiClient.get<DashboardWeeklyApiResponse>(
                    '/api/v1/mob/dashboard/weekly',
                    { params: wParams }
                );
                const suppPerf = wResp.data.supplierPerformances?.[0];
                if (suppPerf?.slotNames?.length) {
                    const countStrs: string[] = suppPerf.dailySlotCounts?.[dateParam] ?? [];
                    policySlots = suppPerf.slotNames.map((name, idx) => {
                        const cs = countStrs[idx] ?? '';
                        const slash = cs.indexOf('/');
                        let cur = 0, tgt = 0;
                        if (slash >= 0) {
                            cur = parseInt(cs.slice(0, slash), 10) || 0;
                            tgt = parseInt(cs.slice(slash + 1), 10) || 0;
                        } else if (cs) {
                            cur = parseInt(cs, 10) || 0;
                        }
                        const slotId = SLOT_IDS_BY_IDX[idx] ?? `slot_${idx}`;
                        return {
                            id: slotId,
                            name,
                            icon: SLOT_ICONS_BY_IDX[idx] ?? '📦',
                            timeRange: (suppPerf.dailySlotTimeRanges?.[dateParam]?.[idx] ?? suppPerf.slotTimeRanges?.[idx]) ?? SLOT_RANGES_BY_IDX[idx] ?? '',
                            current: cur,
                            target: tgt,
                            rate: tgt > 0 ? Math.floor((cur / tgt) * 1000) / 10 : 0,
                            status: calculateSlotStatus(slotId),
                        };
                    });
                }
            } catch {
                // weekly API 실패 시 고정 4슬롯으로 폴백
            }
        }
        // ─────────────────────────────────────────────────────────────────────

        // 폴백: SLOTINFO 기반 고정 4슬롯
        const morningTarget = getSlotTarget('SLOT1');
        const afternoonTarget = getSlotTarget('SLOT2');
        const eveningTarget = getSlotTarget('SLOT3');
        const nightTarget = getSlotTarget('SLOT4');
        const morningCurrent = data.totMorTimeCnt || 0;
        const afternoonCurrent = data.totAfterNonCnt || 0;
        const eveningCurrent = data.totNightNonCnt || 0;
        const nightCurrent = data.totNightCnt || 0;

        const timeSlots: TimeSlot[] = policySlots ?? [
            {
                id: 'morning',
                name: '아침/점심 피크',
                icon: '📦',
                timeRange: isWeekend ? '06:00 ~ 13:59' : '06:00 ~ 12:59',
                current: morningCurrent,
                target: morningTarget,
                rate: morningTarget > 0 ? Math.floor((morningCurrent / morningTarget) * 100 * 10) / 10 : 0,
                status: calculateSlotStatus('morning'),
            },
            {
                id: 'afternoon',
                name: '오후 논피크',
                icon: '📦️',
                timeRange: isWeekend ? '14:00 ~ 16:59' : '13:00 ~ 16:59',
                current: afternoonCurrent,
                target: afternoonTarget,
                rate: afternoonTarget > 0 ? Math.floor((afternoonCurrent / afternoonTarget) * 100 * 10) / 10 : 0,
                status: calculateSlotStatus('afternoon'),
            },
            {
                id: 'evening',
                name: '저녁 피크',
                icon: '📦',
                timeRange: '17:00 ~ 19:59, 평일/주말 동일',
                current: eveningCurrent,
                target: eveningTarget,
                rate: eveningTarget > 0 ? Math.floor((eveningCurrent / eveningTarget) * 100 * 10) / 10 : 0,
                status: calculateSlotStatus('evening'),
            },
            {
                id: 'night',
                name: '야간',
                icon: '📦',
                timeRange: '20:00 ~ 02:59, 평일/주말 동일',
                current: nightCurrent,
                target: nightTarget,
                rate: nightTarget > 0 ? Math.floor((nightCurrent / nightTarget) * 100 * 10) / 10 : 0,
                status: calculateSlotStatus('night'),
            },
        ];

        return {
            dailyStats,
            timeSlots,
            success: true,
        };
    } catch (error) {
        console.error('당일 현황 조회 실패:', error);
        return {
            dailyStats: DEFAULT_DAILY_STATS,
            timeSlots: DEFAULT_TIME_SLOTS,
            success: false,
            message: '당일 현황을 조회하는 중 오류가 발생했습니다.',
        };
    }
};

/**
 * 라이더 현황 조회
 * GET /api/v1/dashboard/summary + GET /api/v1/dashboard/weekly/riders
 *
 * 백엔드 API 2개를 조합하여 라이더 현황 데이터를 구성합니다:
 * - /api/v1/dashboard/summary: 전체 라이더 수 및 상태별 현황
 * - /api/v1/dashboard/weekly/riders: 라이더별 상세 정보
 */
export const getRiderStatus = async (
    params: RiderStatusRequest & { suppId: string; fromDate: string; toDate: string }
): Promise<RiderStatusResponse> => {
    try {
        const adminViewSuppId = localStorage.getItem('ADMIN_VIEW_SUPP_ID');
        const summaryParams = adminViewSuppId ? { suppId: adminViewSuppId } : undefined;
        
        const summaryResponse = await apiClient.get<DashboardSummaryApiResponse>(
            '/api/v1/mob/dashboard/summary',
            summaryParams ? { params: summaryParams } : undefined
        );

        const ridersParams: any = {
            fromDate: params.fromDate,
            toDate: params.toDate,
        };
        if (adminViewSuppId) {
            ridersParams.suppId = adminViewSuppId;
        }
        
        const ridersResponse = await apiClient.get<DashboardWeeklyRidersApiResponse>(
            '/api/v1/mob/dashboard/weekly/riders',
            {
                params: ridersParams,
                timeout: 60000,
            }
        );

        const summaryData = summaryResponse.data;
        const ridersData = ridersResponse.data;

        // 정책 기반 슬롯명 조회 (weekly API 재사용)
        const DEFAULT_RIDER_SLOT_NAMES = ['아침/점심 피크', '오후 논피크', '저녁 피크', '야간'];
        let policySlotNames: string[] = DEFAULT_RIDER_SLOT_NAMES;
        try {
            const wParams : Record<string, string> = { fromDate: params.fromDate, toDate: params.fromDate };
            if (adminViewSuppId) wParams.suppId = adminViewSuppId;
            const wResp = await apiClient.get<DashboardWeeklyApiResponse>(
                '/api/v1/mob/dashboard/weekly',
                { params: wParams }
            );
            const suppPerf = wResp.data.supplierPerformances?.[0];
            if (suppPerf?.slotNames?.length) {
                policySlotNames = suppPerf.slotNames;
            }
        } catch {
            // 슬롯 정책 조회 실패 시 기본 4슬롯 사용
        }

        // 상태별 라이더 수 계산
        // statusSummary에서 운행중(DELIVERY/ACTIVE) 라이더 수를 계산
        let activeRiders = 0;

        if (summaryData.statusSummary) {
            summaryData.statusSummary.forEach((status) => {
                if (status.deliveryStatus === 'DELIVERING' || status.deliveryStatus === 'ACTIVE') {
                    activeRiders += status.riderCnt;
                }
            });
        }

        // 총 라이더 수
        const totalRiders = summaryData.totRiderCnt || 0;

        // 운행종료 라이더 수 = 총 라이더 수 - 운행중 라이더 수
        const completedRiders = totalRiders - activeRiders;

        // 라이더 통계 구성
        const riderStats: RiderStats = {
            totalRiders,
            activeRiders,
            completedRiders: completedRiders >= 0 ? completedRiders : 0,
        };

        // 라이더 목록 변환
        let riders: RiderData[] = [];
        if (ridersData.riderPerformances) {
            riders = ridersData.riderPerformances.map((rider) => {
                // dailySlots에서 슬롯별 건수 합산 (동적 슬롯 수 대응)
                const slotCounts: number[] = policySlotNames.map(() => 0);

                if (rider.dailySlots) {
                    Object.values(rider.dailySlots).forEach((slots) => {
                        if (slots) {
                            slots.forEach((count, idx) => {
                                if (idx < slotCounts.length) {
                                    slotCounts[idx] += count || 0;
                                }
                            });
                        }
                    });
                }

                return {
                    id: rider.riderId,
                    name: rider.riderName,
                    status: mapDeliveryStatusToRiderStatus(rider.deliveryStatus),
                    phoneNumber: rider.riderPhone || '',
                    completedCount: rider.finishCnt,
                    acceptanceRate: rider.acceptanceRate || 0,
                    morningPeakCount: slotCounts[0] ?? 0,
                    afternoonNonPeakCount: slotCounts[1] ?? 0,
                    eveningPeakCount: slotCounts[2] ?? 0,
                    nightCount: slotCounts[3] ?? 0,
                    slotCounts,
                    foodCount: rider.finishFoodCnt || 0,
                    bmartCount: rider.finishBmartCnt || 0,
                    storeCount: rider.finishStoreCnt || 0,
                };
            });
        }

        // 필터 적용
        if (params.filter && params.filter !== '전체') {
            riders = riders.filter((rider) => {
                switch (params.filter) {
                    case '아침점심':
                        return rider.morningPeakCount > 0;
                    case '오후 논피크':
                        return rider.afternoonNonPeakCount > 0;
                    case '저녁 피크':
                        return rider.eveningPeakCount > 0;
                    case '야간':
                        return rider.nightCount > 0;
                    default:
                        return true;
                }
            });
        }

        // 검색어 적용
        if (params.searchQuery) {
            const query = params.searchQuery.toLowerCase();
            riders = riders.filter(
                (rider) =>
                    rider.name.toLowerCase().includes(query) ||
                    rider.phoneNumber.includes(query)
            );
        }

        // 정렬 적용: 운행중(active) 라이더가 항상 먼저 나오도록 1차 정렬
        // 그 다음 사용자가 선택한 정렬 기준으로 2차 정렬
        const statusPriority = (status: RiderStatus): number => {
            if (status === 'active') return 0;  // 운행중 우선
            return 1;   // 그 외 (inactive, completed)
        };

        if (params.sortBy) {
            switch (params.sortBy) {
                case '수락율':
                    riders.sort((a, b) => {
                        const statusDiff = statusPriority(a.status) - statusPriority(b.status);
                        if (statusDiff !== 0) return statusDiff;
                        return b.acceptanceRate - a.acceptanceRate;
                    });
                    break;
                case '거절순':
                    riders.sort((a, b) => {
                        const statusDiff = statusPriority(a.status) - statusPriority(b.status);
                        if (statusDiff !== 0) return statusDiff;
                        const aReject = (100 - a.acceptanceRate) * a.completedCount / 100;
                        const bReject = (100 - b.acceptanceRate) * b.completedCount / 100;
                        return bReject - aReject;
                    });
                    break;
                case '완료순':
                    riders.sort((a, b) => {
                        const statusDiff = statusPriority(a.status) - statusPriority(b.status);
                        if (statusDiff !== 0) return statusDiff;
                        return b.completedCount - a.completedCount;
                    });
                    break;
                case '이름순':
                    riders.sort((a, b) => {
                        const statusDiff = statusPriority(a.status) - statusPriority(b.status);
                        if (statusDiff !== 0) return statusDiff;
                        return a.name.localeCompare(b.name, 'ko');
                    });
                    break;
                default:
                    // 기본 정렬: 운행중 우선, 그 다음 수락율 순
                    riders.sort((a, b) => {
                        const statusDiff = statusPriority(a.status) - statusPriority(b.status);
                        if (statusDiff !== 0) return statusDiff;
                        return b.acceptanceRate - a.acceptanceRate;
                    });
            }
        } else {
            // sortBy가 없을 때도 운행중 우선 정렬 적용
            riders.sort((a, b) => {
                const statusDiff = statusPriority(a.status) - statusPriority(b.status);
                if (statusDiff !== 0) return statusDiff;
                return b.acceptanceRate - a.acceptanceRate;
            });
        }

        return {
            riderStats,
            riders,
            slotNames: policySlotNames,
            success: true,
        };
    } catch (error) {
        console.error('라이더 현황 조회 실패:', error);
        return {
            riderStats: DEFAULT_RIDER_STATS,
            riders: [],
            slotNames: ['아침/점심 피크', '오후 논피크', '저녁 피크', '야간'],
            success: false,
            message: '라이더 현황을 조회하는 중 오류가 발생했습니다.',
        };
    }
};
